// People: everyone delivering leaflets, their logins and how they're doing.
// The list opens to one person's page (jobs, speed, money); PersonModal adds
// or edits someone, and LoginDetails shows a new password once so it can be
// passed on (with a ready-made message for WhatsApp or a text).
import { useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Textarea, ModalShell, Stat, StatRow } from '../lib/ui';
import { formatDay, formatGBP } from '../lib/trade';
import {
  PERSON_STATUSES, COLOURS, APP_URL, personMoney, personStats, formatCount, suggestUsername, loginMessage,
} from '../lib/leaflets';
import { byId, PersonTag, PersonPill, JobPill, JobProgress, Facts } from './LeafletsCommon';
import { Label, errorText } from './TradeCommon';

export function PeopleList({ data, onOpen }) {
  const people = [...data.distributors].sort((a, b) =>
    (a.status === 'left') - (b.status === 'left') || a.name.localeCompare(b.name));
  if (!people.length) {
    return (
      <div className="trade-empty">
        <strong>Nobody yet</strong>
        Add the people delivering leaflets for you. Each gets their own login for the leaflets app.
      </div>
    );
  }
  return (
    <div className="ui-card" style={{ padding: 0, overflowX: 'auto' }}>
      <table className="trade-table">
        <thead>
          <tr>
            <th>Name</th><th>Status</th><th>Happy to do</th><th className="num">In progress</th>
            <th className="num">Delivered</th><th className="num">Per hour</th><th className="num">To pay</th><th>Last in the app</th>
          </tr>
        </thead>
        <tbody>
          {people.map(p => {
            const stats = personStats(p._id, data.jobs);
            const money = personMoney(p._id, data.jobs);
            const active = data.jobs.filter(j => j.distributor === p._id && j.status === 'active').length;
            return (
              <tr key={p._id} onClick={() => onOpen(p._id)}>
                <td><PersonTag person={p} /><span className="sub">{p.username}</span></td>
                <td><PersonPill status={p.status} /></td>
                <td className="lf-clip">{p.areas || <span className="lf-muted">Anywhere</span>}</td>
                <td className="num">{active || ''}</td>
                <td className="num">{formatCount(stats.delivered)}</td>
                <td className="num">{stats.perHour ?? ''}</td>
                <td className="num">{money.owed ? formatGBP(money.owed) : ''}</td>
                <td>{p.lastLoginAt ? formatDay(p.lastLoginAt) : <span className="lf-muted">Never</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function PersonDetail({ person, data, actions, onBack }) {
  const jobs = data.jobs.filter(j => j.distributor === person._id);
  const zones = byId(data.zones);
  const payouts = data.payouts.filter(p => p.distributor === person._id);
  const stats = personStats(person._id, data.jobs);
  const money = personMoney(person._id, data.jobs);
  const trialDone = person.status === 'trial' && jobs.some(j => j.kind === 'trial' && j.status === 'done');

  return (
    <>
      <Button size="sm" variant="muted" className="trade-back" onClick={onBack}>&larr; Everyone</Button>
      <div className="trade-hero">
        <div className="trade-hero__top">
          <div>
            <h2><PersonTag person={person} /></h2>
            <div className="trade-hero__contact">
              <PersonPill status={person.status} />
              {person.phone && <a href={`tel:${person.phone.replace(/\s/g, '')}`}>{person.phone}</a>}
              {person.email && <a href={`mailto:${person.email}`}>{person.email}</a>}
              <span>Username <b>{person.username}</b></span>
              <span>{person.lastLoginAt ? `Last in the app ${formatDay(person.lastLoginAt)}` : 'Hasn\'t logged in yet'}</span>
            </div>
          </div>
          <div className="lf-row">
            <Button onClick={() => actions.editPerson(person)}>Edit</Button>
            <Button onClick={() => actions.resetPassword(person)}>New password</Button>
            {person.status !== 'left' && <Button variant="primary" onClick={() => actions.assign({ distributor: person._id })}>Give out leaflets</Button>}
          </div>
        </div>
        {person.areas && <div className="trade-hero__facts"><div><span>Happy to do</span>{person.areas}</div></div>}
      </div>

      {trialDone && (
        <div className="ui-banner ui-banner--info lf-row" style={{ marginBottom: 16 }}>
          The trial job is done. Happy with it? Mark {person.name.split(' ')[0]} as active to give out regular jobs.
          <Button size="sm" style={{ marginLeft: 'auto' }} onClick={() => actions.setPersonStatus(person, 'active')}>Mark active</Button>
        </div>
      )}

      <StatRow>
        <Stat label="Delivered" value={formatCount(stats.delivered)} sub={`${stats.rounds} round${stats.rounds === 1 ? '' : 's'} approved`} />
        <Stat label="Speed" value={stats.perHour ? `${stats.perHour}/hr` : '-'} sub={stats.perKm ? `${stats.perKm} per km walked` : 'from the times they log'} />
        <Stat label="Earned" value={formatGBP(money.earned)} sub={`${formatGBP(money.paid)} paid`} />
        <Stat label="To pay" value={formatGBP(money.owed)} accent={money.owed ? 'var(--warn)' : undefined}
          sub={money.waiting ? `plus ${formatGBP(money.waiting)} waiting to be checked` : 'nothing waiting'} />
      </StatRow>

      <div className="trade-detail">
        <div className="trade-detail__col">
          <div className="ui-card">
            <h3>Jobs</h3>
            {jobs.length ? (
              <ul className="trade-mini-list">
                {jobs.map(j => (
                  <li key={j._id} onClick={() => actions.openJob(j._id)}>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <b>{zones[j.zone]?.name || 'Deleted zone'}</b>
                      <span className="lf-muted"> &middot; {formatCount(j.leaflets)} given {formatDay(j.givenOn)}{j.kind === 'trial' ? ' (trial)' : ''}</span>
                      <JobProgress job={j} compact />
                    </span>
                    <JobPill job={j} />
                  </li>
                ))}
              </ul>
            ) : <p className="lf-muted">No jobs yet.</p>}
          </div>
        </div>
        <div className="trade-detail__col">
          <div className="ui-card">
            <h3>Payments</h3>
            {payouts.length ? (
              <ul className="trade-mini-list">
                {payouts.map(p => (
                  <li key={p._id} onClick={actions.openPayments}>
                    {formatDay(p.paidOn)}<span className="lf-muted">{p.runs.length} round{p.runs.length === 1 ? '' : 's'}</span>
                    <span className="amt">{formatGBP(p.amount)}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="lf-muted">Nothing paid yet.</p>}
            {money.owed > 0 && <Button size="sm" variant="primary" style={{ marginTop: 10 }} onClick={() => actions.pay(person)}>Pay {formatGBP(money.owed)}</Button>}
          </div>
          {person.notes && <div className="ui-card"><h3>Notes</h3><p className="lf-pre">{person.notes}</p></div>}
        </div>
      </div>
    </>
  );
}

// The username and password, once, plus the message to send them.
export function LoginDetails({ person, password }) {
  const [copied, setCopied] = useState(false);
  const message = loginMessage(person, password);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="lf-login">
      <Facts items={[['App', APP_URL.replace('https://', '')], ['Username', <b key="u">{person.username}</b>], ['Password', <code key="p" className="lf-password">{password}</code>]]} />
      <p className="trade-form__hint">This is the only time the password is shown. If it's lost, make a new one.</p>
      <Label>Message to send them</Label>
      <Textarea rows={5} readOnly value={message} onFocus={e => e.target.select()} />
      <div className="lf-row" style={{ marginTop: 8 }}>
        <Button size="sm" onClick={copy}>{copied ? 'Copied' : 'Copy message'}</Button>
      </div>
    </div>
  );
}

export function PersonModal({ person, onClose, onSaved }) {
  const adding = !person;
  const [f, setF] = useState(() => ({
    name: person?.name || '',
    username: person?.username || '',
    phone: person?.phone || '',
    email: person?.email || '',
    status: person?.status || 'trial',
    areas: person?.areas || '',
    ratePer1000: person?.ratePer1000 ?? '',
    colour: person?.colour || '',
    notes: person?.notes || '',
    password: '',
  }));
  const [typedUsername, setTypedUsername] = useState(!adding);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null); // { distributor, password }

  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const setName = (e) => {
    const name = e.target.value;
    setF(p => ({ ...p, name, ...(typedUsername ? {} : { username: suggestUsername(name) }) }));
  };

  const save = async () => {
    if (!f.name.trim()) return setError('Add their name');
    setSaving(true);
    setError('');
    const { password, ...rest } = f;
    try {
      if (adding) {
        const { data } = await api.post('/leaflets/admin/distributors', { ...rest, ...(password ? { password } : {}) });
        if (data.password) {
          setCreated(data);
          setSaving(false);
        } else {
          onSaved(data.distributor, true);
        }
      } else {
        const { data } = await api.patch(`/leaflets/admin/distributors/${person._id}`, rest);
        onSaved(data.distributor, false);
      }
    } catch (e) {
      setError(errorText(e));
      setSaving(false);
    }
  };

  if (created) {
    return (
      <ModalShell onClose={() => onSaved(created.distributor, true)} width={560} ariaLabel="Login details">
        <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>{created.distributor.name} is set up</h2>
        <LoginDetails person={created.distributor} password={created.password} />
        <div className="trade-form__actions">
          <Button variant="primary" onClick={() => onSaved(created.distributor, true)}>Done</Button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} width={600} ariaLabel={adding ? 'Add someone' : 'Edit person'}>
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>{adding ? 'Add someone' : `Edit ${person.name}`}</h2>
      <div className="trade-form">
        <div className="inv-grid2">
          <div><Label>Name</Label><Input autoFocus value={f.name} onChange={setName} placeholder="Sarah Jones" /></div>
          <div>
            <Label hint="they log in with this">Username</Label>
            <Input value={f.username} onChange={e => { setTypedUsername(true); set('username')(e); }} placeholder="sarahj" />
          </div>
          <div><Label>Phone</Label><Input type="tel" value={f.phone} onChange={set('phone')} /></div>
          <div><Label hint="optional">Email</Label><Input type="email" value={f.email} onChange={set('email')} /></div>
        </div>
        <div>
          <Label>Status</Label>
          <div className="trade-seg">
            {PERSON_STATUSES.map(s => (
              <button key={s.key} type="button" className={f.status === s.key ? 'is-on' : ''} title={s.hint} onClick={() => setF(p => ({ ...p, status: s.key }))}>{s.label}</button>
            ))}
          </div>
          <div className="trade-form__hint">{PERSON_STATUSES.find(s => s.key === f.status)?.hint}</div>
        </div>
        <div><Label hint="in their words">Happy to do</Label><Input value={f.areas} onChange={set('areas')} placeholder="e.g. Hullbridge only, weekday mornings" /></div>
        <div className="inv-grid2">
          <div>
            <Label hint="£ per 1000, for new jobs">Their pay</Label>
            <div className="pl-price"><Input type="number" min="0" step="0.5" value={f.ratePer1000} onChange={set('ratePer1000')} placeholder="The default" /></div>
          </div>
          <div>
            <Label hint="on the map">Colour</Label>
            <div className="lf-colours">
              {COLOURS.map(c => (
                <button key={c} type="button" className={f.colour === c ? 'is-on' : ''} style={{ background: c }}
                  onClick={() => setF(p => ({ ...p, colour: c }))} aria-label={`Colour ${c}`} />
              ))}
            </div>
          </div>
        </div>
        {adding && (
          <div>
            <Label hint="leave empty and one is made up for you">Password</Label>
            <Input value={f.password} onChange={set('password')} placeholder="Make one up for me" autoComplete="new-password" />
          </div>
        )}
        <div><Label hint="only in Farm OS">Notes</Label><Textarea rows={2} value={f.notes} onChange={set('notes')} /></div>
        {error && <div className="ui-banner ui-banner--bad">{error}</div>}
      </div>
      <div className="trade-form__actions">
        <Button variant="muted" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : adding ? 'Add and make a login' : 'Save'}</Button>
      </div>
    </ModalShell>
  );
}

export function PasswordModal({ person, onClose, onDone }) {
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const go = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post(`/leaflets/admin/distributors/${person._id}/password`, typed ? { password: typed } : {});
      setResult(data);
      onDone(data.distributor);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={560} ariaLabel="New password">
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>New password for {person.name}</h2>
      {result ? <LoginDetails person={result.distributor} password={result.password} /> : (
        <div className="trade-form">
          <p className="lf-muted">The old password stops working, and they're logged out of the app on their phone until they log in with the new one.</p>
          <div>
            <Label hint="leave empty and one is made up for you">Password</Label>
            <Input value={typed} onChange={e => setTyped(e.target.value)} placeholder="Make one up for me" autoComplete="new-password" />
          </div>
          {error && <div className="ui-banner ui-banner--bad">{error}</div>}
        </div>
      )}
      <div className="trade-form__actions">
        {result
          ? <Button variant="primary" onClick={onClose}>Done</Button>
          : <><Button variant="muted" onClick={onClose}>Cancel</Button><Button variant="primary" onClick={go} disabled={saving}>{saving ? 'Saving…' : 'Make the new password'}</Button></>}
      </div>
    </ModalShell>
  );
}
